import { useTrackEvent } from "@outschool/ui-analytics";
import { CategoryPartial } from "./navbarCategories";
import { hasSubcategories } from "./categoryNavbarUtils";

export const CATEGORY_NAVBAR_COMPONENT = {
  BROWSE_MENU: "browse-menu",
  HORIZONTAL_NAVBAR: "horizontal-navbar",
} as const;

export type CategoryNavbarComponent =
  (typeof CATEGORY_NAVBAR_COMPONENT)[keyof typeof CATEGORY_NAVBAR_COMPONENT];

/**
 * Builds tracking properties for category navbar events with consistent hierarchy
 */
export function buildCategoryTrackingProperties(
  category: CategoryPartial,
  options?: {
    component?: CategoryNavbarComponent;
    ancestors?: CategoryPartial[];
  },
): Record<string, any> {
  const properties: Record<string, any> = {
    category: category.slug,
  };

  if (options?.component) {
    properties.component = options.component;
  }

  if (options?.ancestors && options.ancestors.length > 0) {
    properties.ancestors = options.ancestors.map(ancestor => ancestor.slug);
  }

  return properties;
}

/**
 * Tracks when a submenu is opened (on desktop hover or mobile tap)
 * For desktop with a component: Only fires if the category has subcategories and wasn't already selected
 * For mobile or without component: Always fires
 */
export function trackSubmenuOpen(
  trackEvent: ReturnType<typeof useTrackEvent>,
  category: CategoryPartial,
  options?: {
    component?: CategoryNavbarComponent;
    ancestors?: CategoryPartial[];
    wasAlreadySelected?: boolean;
  },
): void {
  // Desktop with component only tracks if category has subcategories and wasn't already selected
  if (options?.component && options?.wasAlreadySelected !== undefined) {
    if (!hasSubcategories(category) || options.wasAlreadySelected) {
      return;
    }
  }

  const properties = buildCategoryTrackingProperties(category, options);

  trackEvent("category_navbar_submenu_open", properties);
}
